import { Fragment, useEffect, useMemo, useRef, useState, useCallback, type ChangeEvent } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormDraft, DRAFT_KEY_PREFIX } from '@/hooks/useFormDraft';
import { useDirtyGuard } from '@/hooks/useDirtyGuard';
import { useAppSelector, useAppDispatch } from '@/features/hooks';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { DraftRestoredBanner } from '@/components/common/DraftRestoredBanner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { FieldTooltip } from '@/components/ui/field-tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { CalendarIcon, Loader2, Plus, X, ChevronDown, Paperclip, Eye, Trash2, Camera, Tag } from 'lucide-react';
import { cn, humanizeAccountType } from '@/lib/utils';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { TagInput } from '@/components/ui/tag-input';
import { IconButton } from '@/components/ui/icon-button';
import { useCurrency } from '@/hooks/useCurrency';
import { useTimezone } from '@/hooks/useTimezone';
import { useDisplaySettings } from '@/hooks/useDisplaySettings';
import { useToast } from '@/hooks/use-toast';
import { getCurrencyStep, getCurrencyMinAmount, getCurrencySymbol } from '@/lib/utils';
import type { Transaction, Account, Category } from '@/lib/types';
import type { ModalEntry } from '@/hooks/useModalStack';
import api, { endpoints } from '@/services/api';
import { unlinkReceipt } from '@/features/receipts/receiptsSlice';
import { CurrencySelect } from '@/components/ui/currency-select';
import ReceiptViewerDialog from '@/components/transactions/ReceiptViewerDialog';
import { ReceiptPreviewPane } from '@/components/transactions/ReceiptPreviewPane';
import { TransactionFieldRow, type EditRendererArgs, type FieldSpec } from '@/components/transactions/TransactionFieldRow';
import { SecondaryFieldsDisclosure } from '@/components/transactions/SecondaryFieldsDisclosure';
import { PRIMARY_FIELDS, SECONDARY_FIELDS } from '@/components/transactions/fieldSpec';


const itemSchema = z.object({
  description: z.string().min(1, 'Required').max(200),
  amount: z.coerce.number().positive('Must be positive'),
  discountAmount: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce.number().min(0, 'Discount cannot be negative').optional(),
  ),
  quantity: z.coerce.number().int().min(1).max(9999).optional().default(1),
  categoryId: z.string().optional(),
  notes: z.string().max(500).optional(),
  emoji: z.string().optional(),
});

const transactionSchema = z.object({
  type: z.enum(['income', 'expense', 'transfer']),
  amount: z.coerce
    .number({ invalid_type_error: 'Amount must be a number' })
    .positive('Amount must be positive'),
  discountAmount: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce.number().min(0, 'Discount cannot be negative').optional(),
  ),
  description: z.string().min(1, 'Description is required').max(200),
  date: z.date({ required_error: 'Date is required' }),
  accountId: z.string().min(1, 'Account is required'),
  categoryId: z.string().optional(),
  currency: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().length(3, 'Use a 3-letter currency code').optional(),
  ),
  notes: z.string().max(500).optional(),
  isRecurring: z.boolean().optional(),
  emoji: z.string().optional(),
  tags: z.array(z.string().max(30)).max(10).optional(),
  pendingReceiptId: z.string().optional(),
  pendingReceiptFilename: z.string().optional(),
  pendingReceiptMimeType: z.string().optional(),
  pendingReceiptSize: z.number().optional(),
  // Transfer-specific
  toAccountId: z.string().optional(),
  // T80P1: Line items
  items: z.array(itemSchema).max(50).optional(),
}).superRefine((data, ctx) => {
  const transactionDiscount = data.discountAmount ?? 0;
  if (transactionDiscount > data.amount + 0.001) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Discount cannot exceed transaction amount',
      path: ['discountAmount'],
    });
  }

  if (data.items && data.items.length > 0 && data.amount > 0) {
    const transactionGrossCap = Math.max(0, data.amount);
    const itemsNetTotal = data.items.reduce((sum, item, index) => {
      const itemGross = (item.amount || 0) * (item.quantity ?? 1);
      const itemDiscount = item.discountAmount ?? 0;

      if (itemDiscount > itemGross + 0.001) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Item discount cannot exceed item gross',
          path: ['items', index, 'discountAmount'],
        });
      }

      return sum + Math.max(0, itemGross - itemDiscount);
    }, 0);

    if (itemsNetTotal > transactionGrossCap + 0.001) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Items net total exceeds transaction gross amount',
        path: ['items'],
      });
    }
  }
});

export type TransactionFormData = z.infer<typeof transactionSchema>;

interface TransactionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TransactionFormData) => Promise<void>;
  transaction?: Transaction | null;
  /** Pre-fill values when creating a new transaction (e.g. from receipt scan) */
  initialValues?: Partial<TransactionFormData>;
  accounts: Account[];
  categories: Category[];
  isLoading?: boolean;
  stack?: ModalEntry[];
  onBack?: () => void;
}

export default function TransactionForm({
  open,
  onOpenChange,
  onSubmit,
  transaction,
  initialValues,
  accounts,
  categories,
  isLoading = false,
  stack,
  onBack,
}: TransactionFormProps) {
  const isEditing = !!transaction;
  const { currency: userCurrency } = useCurrency();
  const { formatInUserTimezone } = useTimezone();
  const { itemDisplayDensity } = useDisplaySettings();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const dispatch = useAppDispatch();

  const form = useForm<TransactionFormData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      type: 'expense',
      amount: 0,
      discountAmount: undefined,
      description: '',
      date: new Date(),
      accountId: '',
      categoryId: '',
      currency: '',
      notes: '',
      isRecurring: false,
      toAccountId: '',
      emoji: '',
      tags: [],
      pendingReceiptId: '',
      pendingReceiptFilename: '',
      pendingReceiptMimeType: '',
      items: [],
    },
  });

  // T80P1: Items field array
  // T137: Guard against redundant form.reset() on same-transaction reference changes.
  // Tracks the last transaction ID we initialized the form for so re-renders of the
  // same transaction (e.g. Redux list refresh with new object reference) don't wipe
  // in-flight user edits like quantity.
  const initializedForTxnRef = useRef<string | null>(null);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  // T80P1: Items toggle state
  const [itemsEnabled, setItemsEnabled] = useState(false);
  // T80P1: Track which items have "show more" expanded
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  // T80P1: Confirmation state for removing all items
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  // T80P1: Ref for item description inputs for focus management
  const itemDescRefs = useRef<(HTMLInputElement | null)[]>([]);
  // T80P1: Ref for item delete buttons for focus management
  const itemDeleteRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // T115-F4-e: Tag suggestions for autocomplete
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [tagSuggestionStats, setTagSuggestionStats] = useState<{ tag: string; count: number }[]>([]);
  useEffect(() => {
    if (!open) return;
    const rafId = requestAnimationFrame(() => {
      Promise.all([
        api.get('/transactions/tags'),
        api.get('/transactions/analytics/tags-summary'),
      ]).then(([tagsRes, summaryRes]) => {
        setTagSuggestions((tagsRes.data as { data?: string[] })?.data ?? []);
        setTagSuggestionStats(summaryRes.data?.data?.tags ?? []);
      }).catch(() => { /* ignore */ });
    });
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [open]);

  const { toast } = useToast();

  // T127: Receipt upload state (edit mode only)
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptDeleting, setReceiptDeleting] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const receiptViewTriggerRef = useRef<HTMLButtonElement>(null);

  // Receipt viewer dialog state
const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
  // [rev20 L3-B] Mobile/tablet preview disclosure state. Reset on modal close via the open-effect below.
  const [previewOpen, setPreviewOpen] = useState(false);
  // [rev20 L3-B desktop-mount] Reactive desktop detection. The mobile Preview button is unmounted
  // entirely at desktop so the E2E `toHaveCount(0)` contract holds (CSS-hide alone keeps the node
  // in the DOM). `lg:hidden` on the button remains as a defensive CSS safety boundary.
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Initialize receipt state from transaction data when editing or a pending create draft
  useEffect(() => {
    if (!open) {
      setReceiptId(null);
      setReceiptViewerOpen(false);
      // [rev20 L3-B] Collapse the preview disclosure so the next open starts hidden on mobile/tablet.
      setPreviewOpen(false);
      return;
    }

    if (!transaction) {
      setReceiptId(initialValues?.pendingReceiptId || null);
      return;
    }
    // If transaction has a receiptAttachment, look up the receipt document ID
    if (transaction.receiptAttachment?.filename) {
      let cancelled = false;
      api.get(endpoints.receipts.list, { params: { transactionId: transaction.id, limit: 1 } })
        .then((res) => {
          if (cancelled) return;
          const receipts = (res.data as { data?: { _id: string }[] })?.data;
          if (receipts && receipts.length > 0) {
            setReceiptId(receipts[0]._id);
          }
        })
        .catch(() => { /* ignore */ });
      return () => { cancelled = true; };
    } else {
      setReceiptId(null);
    }
  }, [initialValues?.pendingReceiptId, open, transaction]);

  const handleReceiptUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';

      const MAX_SIZE = 10 * 1024 * 1024;
      const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
      if (!ALLOWED.includes(file.type) || file.size > MAX_SIZE) return;

      setReceiptUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (transaction?.id) {
          formData.append('transactionId', transaction.id);
        }
        const res = await api.post(endpoints.receipts.upload, formData, { timeout: 60000 });
        const newReceipt = (res.data as {
          data?: {
            _id?: string;
            id?: string;
            originalFilename?: string;
            filename?: string;
            mimeType?: string;
            mimetype?: string;
            originalSize?: number;
            fileSize?: number;
          };
        })?.data;
        const rid = newReceipt?._id ?? newReceipt?.id ?? null;
        if (rid) {
          setReceiptId(rid);
          if (!transaction) {
            form.setValue('pendingReceiptId', rid, { shouldDirty: true });
            form.setValue('pendingReceiptFilename', newReceipt?.originalFilename ?? newReceipt?.filename ?? file.name, { shouldDirty: true });
            form.setValue('pendingReceiptMimeType', newReceipt?.mimeType ?? newReceipt?.mimetype ?? file.type, { shouldDirty: true });
            form.setValue('pendingReceiptSize', newReceipt?.originalSize ?? newReceipt?.fileSize ?? file.size, { shouldDirty: true });
          }
        } else {
          toast({ title: 'Receipt uploaded', description: 'Refresh to see the attachment.' });
        }
      } catch (err) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? (err instanceof Error ? err.message : 'Upload failed');
        toast({ title: 'Receipt upload failed', description: msg, variant: 'destructive' });
      } finally {
        setReceiptUploading(false);
      }
    },
    [form, toast, transaction],
  );

  // Helper to lazily fetch receiptId if it wasn't resolved during init (e.g. 5xx)
  const ensureReceiptId = useCallback(async (): Promise<string | null> => {
    if (receiptId) return receiptId;
    const pendingReceiptId = form.getValues('pendingReceiptId');
    if (pendingReceiptId) {
      setReceiptId(pendingReceiptId);
      return pendingReceiptId;
    }
    if (!transaction?.id) return null;
    try {
      const res = await api.get(endpoints.receipts.list, { params: { transactionId: transaction.id, limit: 1 } });
      const receipts = (res.data as { data?: { _id: string }[] })?.data;
      if (receipts && receipts.length > 0) {
        setReceiptId(receipts[0]._id);
        return receipts[0]._id;
      }
    } catch {
      /* will be handled by caller */
    }
    return null;
  }, [form, receiptId, transaction?.id]);

  const handleReceiptView = useCallback(async () => {
    const rid = await ensureReceiptId();
    if (!rid) {
      toast({ title: 'Receipt unavailable', description: 'Could not load receipt. Please try again later.', variant: 'destructive' });
      return;
    }
    setReceiptViewerOpen(true);
  }, [ensureReceiptId, toast]);

  const handleReceiptDelete = useCallback(async () => {
    const rid = await ensureReceiptId();
    if (!rid) {
      toast({ title: 'Remove failed', description: 'Could not locate receipt to remove.', variant: 'destructive' });
      return;
    }
    setReceiptDeleting(true);
    try {
      if (!transaction) {
        setReceiptId(null);
        form.setValue('pendingReceiptId', '', { shouldDirty: true });
        form.setValue('pendingReceiptFilename', '', { shouldDirty: true });
        form.setValue('pendingReceiptMimeType', '', { shouldDirty: true });
        form.setValue('pendingReceiptSize', undefined, { shouldDirty: true });
        toast({ title: 'Receipt unlinked', description: 'The file remains in your receipt library.' });
        return;
      }
      // Unlink from this transaction. The file remains in the receipt library and
      // can be re-linked later. Backend response shape:
      //   POST /receipts/:id/unlink -> { data: Receipt }
      await dispatch(unlinkReceipt(rid)).unwrap();
      setReceiptId(null);
    } catch {
      toast({ title: 'Remove failed', description: 'Could not remove the receipt link.', variant: 'destructive' });
    } finally {
      setReceiptDeleting(false);
    }
  }, [ensureReceiptId, dispatch, form, toast, transaction]);

  const { clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore } = useFormDraft<TransactionFormData>(
    'transaction-create',
    userId,
    form,
    !isEditing && open
  );

  const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard, saveDraftAndClose } =
    useDirtyGuard(form, onOpenChange, clearDraft, saveDraftNow);

  const clampNonNegative = (value: number) => (Number.isFinite(value) ? Math.max(value, 0) : 0);

  const watchType = form.watch('type');
  const watchAmount = Math.abs(Number(form.watch('amount')) || 0);
  const watchTransactionDiscount = clampNonNegative(Number(form.watch('discountAmount')) || 0);
  const watchItems = form.watch('items') || [];
  const pendingReceiptId = form.watch('pendingReceiptId') || '';
  const pendingReceiptFilename = form.watch('pendingReceiptFilename') || '';
  const pendingReceiptMimeType = form.watch('pendingReceiptMimeType') || '';
  const pendingReceiptSize = form.watch('pendingReceiptSize');

  // Derive active currency: form override → account → user preference
  const watchAccountId = form.watch('accountId');
  const watchCurrency = form.watch('currency');
  const selectedAccount = accounts.find((a) => a.id === watchAccountId);
  const accountCurrency = selectedAccount?.currency || userCurrency;
  const activeCurrency = (watchCurrency && watchCurrency.length === 3) ? watchCurrency : accountCurrency;

  const currencySymbol = getCurrencySymbol(activeCurrency);
  const currencyStep = getCurrencyStep(activeCurrency);
  const currencyMin = getCurrencyMinAmount(activeCurrency);
  const formatActiveMoney = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);

  // T80P1: Compute items summary
  const itemSubtotal = watchItems.reduce(
    (sum, item) => sum + (Number(item.amount) || 0) * (Number(item.quantity) || 1),
    0,
  );
  const itemDiscountTotal = watchItems.reduce(
    (sum, item) => sum + clampNonNegative(Number(item.discountAmount) || 0),
    0,
  );
  const itemNetTotal = itemSubtotal - itemDiscountTotal;
  const transactionNetCap = Math.max(0, watchAmount);
  const remaining = transactionNetCap - itemNetTotal;
  const itemizationPercent = transactionNetCap > 0 ? Math.min((itemNetTotal / transactionNetCap) * 100, 100) : 0;
  const isOverBudget = itemNetTotal > transactionNetCap + 0.001;

  // T80P1: Toggle items callback
  const handleItemsToggle = useCallback((checked: boolean) => {
    if (checked) {
      setItemsEnabled(true);
      if (fields.length === 0) {
        append({ description: '', amount: 0, discountAmount: 0, quantity: 1, categoryId: '', notes: '', emoji: '' });
        setExpandedItems(new Set([0]));
        // Focus first item after render
        requestAnimationFrame(() => {
          itemDescRefs.current[0]?.focus();
        });
      }
    } else {
      if (fields.length > 0) {
        setShowRemoveConfirm(true);
      } else {
        setItemsEnabled(false);
      }
    }
  }, [fields.length, append]);

  // T80P1: Confirm remove all items
  const handleConfirmRemove = useCallback(() => {
    // Remove all items
    for (let i = fields.length - 1; i >= 0; i--) {
      remove(i);
    }
    setItemsEnabled(false);
    setShowRemoveConfirm(false);
    setExpandedItems(new Set());
  }, [fields.length, remove]);

  // T80P1: Add new item with focus
  const handleAddItem = useCallback(() => {
    append({ description: '', amount: 0, discountAmount: 0, quantity: 1, categoryId: '', notes: '', emoji: '' });
    const newIndex = fields.length;
    setExpandedItems(new Set([newIndex]));
    requestAnimationFrame(() => {
      itemDescRefs.current[newIndex]?.focus();
    });
  }, [fields.length, append]);

  // T80P1: Remove item with focus management
  const handleRemoveItem = useCallback((index: number) => {
    remove(index);
    setExpandedItems((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    // Focus management: focus previous item's delete button, or Add Item button 
    requestAnimationFrame(() => {
      if (index > 0) {
        itemDeleteRefs.current[index - 1]?.focus();
      }
    });
  }, [remove]);

  // T119-7: Auto-expand first item with validation error on submit
  useEffect(() => {
    if (form.formState.submitCount === 0) return;
    const itemErrors = form.formState.errors.items;
    if (itemErrors && Array.isArray(itemErrors)) {
      const firstErrorIdx = itemErrors.findIndex(Boolean);
      if (firstErrorIdx >= 0) {
        setExpandedItems(new Set([firstErrorIdx]));
      }
    }
  }, [form.formState.submitCount, form.formState.errors.items]);

  // Populate form when opening
  useEffect(() => {
    if (!open) {
      // Reset the guard on close so next open always re-initializes
      initializedForTxnRef.current = null;
    }

    if (transaction) {
      // T137: Only reset when opening for a different/new transaction.
      // Skipping reset for same-ID re-renders prevents in-flight edits
      // (e.g. quantity changes) from being wiped by Redux reference updates.
      if (initializedForTxnRef.current === transaction.id) return;
      initializedForTxnRef.current = transaction.id;

      // Edit mode: always populate with transaction data
      const txnItems = transaction.items?.map((item) => ({
        description: item.description,
        amount: item.amount,
        discountAmount: item.discountAmount ?? 0,
        quantity: item.quantity || 1,
        categoryId: item.categoryId || '',
        notes: item.notes || '',
        emoji: item.emoji || '',
      })) || [];
      form.reset({
        type: transaction.type,
        amount: Math.abs(transaction.amount),
        discountAmount: transaction.discountAmount ?? undefined,
        description: transaction.description,
        date: new Date(transaction.date),
        accountId: transaction.accountId,
        categoryId: transaction.categoryId || '',
        currency: transaction.currency || '',
        notes: transaction.notes || '',
        isRecurring: transaction.isRecurring || false,
        emoji: transaction.emoji || '',
        tags: transaction.tags || [],
        pendingReceiptId: '',
        pendingReceiptFilename: '',
        pendingReceiptMimeType: '',
        pendingReceiptSize: undefined,
        items: txnItems,
      });
      setItemsEnabled(txnItems.length > 0);
      if (txnItems.length === 1) {
        setExpandedItems(new Set([0]));
      }
    } else if (initialValues) {
      // Create mode with pre-filled values from receipt scan — always takes precedence
      // over any stored draft since this is an explicit user action.
      form.reset({
        type: 'expense',
        amount: 0,
        discountAmount: undefined,
        description: '',
        date: new Date(),
        accountId: accounts[0]?.id || '',
        categoryId: '',
        currency: '',
        notes: '',
        isRecurring: false,
        toAccountId: '',
        emoji: '',
        tags: [],
        pendingReceiptId: '',
        pendingReceiptFilename: '',
        pendingReceiptMimeType: '',
        pendingReceiptSize: undefined,
        items: [],
        ...initialValues,
      });
      setReceiptId(initialValues.pendingReceiptId || null);
      setItemsEnabled(!!initialValues.items && initialValues.items.length > 0);
    } else {
      // Create mode: skip reset if useFormDraft has a draft to restore.
      // useFormDraft's restoration effect runs BEFORE this effect and calls
      // form.reset(draftValues). If we reset to defaults here, we wipe the draft.
      const draftKey = `${DRAFT_KEY_PREFIX}transaction-create:${userId ?? 'anon'}`;
      const hasStoredDraft = Boolean(localStorage.getItem(draftKey));
      if (!hasStoredDraft) {
        form.reset({
          type: 'expense',
          amount: 0,
          discountAmount: undefined,
          description: '',
          date: new Date(),
          accountId: accounts[0]?.id || '',
          categoryId: '',
          currency: '',
          notes: '',
          isRecurring: false,
          toAccountId: '',
          emoji: '',
          tags: [],
          pendingReceiptId: '',
          pendingReceiptFilename: '',
          pendingReceiptMimeType: '',
          pendingReceiptSize: undefined,
          items: [],
        });
        setItemsEnabled(false);
      } else {
        // Draft restoration — check if items are present
        const draftStr = localStorage.getItem(draftKey);
        if (draftStr) {
          try {
            const draftData = JSON.parse(draftStr);
            setItemsEnabled(Array.isArray(draftData?.data?.items) && draftData.data.items.length > 0);
          } catch { /* ignore */ }
        }
      }
    }
    setExpandedItems(new Set());
    setShowRemoveConfirm(false);
  }, [transaction, open, accounts, form, userId, initialValues]);

  const handleSubmit = async (data: TransactionFormData) => {
    await onSubmit(data);
    clearDraft();
    form.reset();
    setItemsEnabled(false);
    setExpandedItems(new Set());
  };

  const filteredCategories = categories.filter(
    (cat) => watchType === 'transfer' || cat.type === watchType
  );

  // rev16 Phase F: Secondary fields (currency, discountAmount) wrapped by SecondaryFieldsDisclosure.
  // Mirrors the legacy standalone blocks: CurrencySelect uses 'currency-select-transaction' testId;
  // discountAmount keeps the exact 'transaction-discount-input' testId and number semantics.
  // Transfers never show a transaction-level discount, so the entry is filtered out.
  const secondaryFields = useMemo<FieldSpec[]>(() => {
    return SECONDARY_FIELDS
      .filter((spec) => !(spec.key === 'discountAmount' && watchType === 'transfer'))
      .map((spec) => {
        if (spec.key === 'currency') {
          return {
            ...spec,
            editRenderer: ({ field }) => {
              const currencyValue = (field.value as string | undefined) ?? '';
              return (
                <CurrencySelect
                  value={currencyValue}
                  onChange={field.onChange}
                  allowAccountDefault
                  accountCurrency={accountCurrency}
                  testId="currency-select-transaction"
                />
              );
            },
          };
        }
        if (spec.key === 'discountAmount') {
          return {
            ...spec,
            editRenderer: ({ field }) => (
              <Input
                type="number"
                step={currencyStep}
                min={0}
                placeholder="0.00"
                data-testid="transaction-discount-input"
                value={(field.value as number | string | undefined) ?? ''}
                onChange={(e) => field.onChange(e.target.value === '' ? undefined : e.target.value)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            ),
          };
        }
        return spec;
      });
  }, [watchType, currencyStep, accountCurrency]);

// [rev17 plan D4] Honor the modal-stack Edit `focusField` metadata by focusing the matching
// txn-field-* control once the Sheet opens. Derived only from the topmost `kind: 'edit'`
// entry in `stack` so Add, Detail, and unrelated Edit re-opens remain unaffected.
const focusField = useMemo(() => {
  if (!stack || stack.length === 0) return undefined;
  const top = stack[stack.length - 1];
  return top.kind === 'edit' && top.focusField ? top.focusField : undefined;
}, [stack]);

// [rev17 plan D5] Mount-only primary-field bootstrap: when the Edit shortcut opens the form with
// a `focusField`, render only the matching primary field on the very first frame so the focused
// control is in the DOM as cheaply as possible. On the next animation frame, restore the full
// canonical primary-field sequence. Add, Detail, and non-focused Edit never carry a focusField,
// so this state stays null and the existing render path is untouched.
const [primaryBootstrapField, setPrimaryBootstrapField] = useState<string | null>(() => {
  if (!stack || stack.length === 0) return null;
  const top = stack[stack.length - 1];
  return top.kind === 'edit' && top.focusField ? top.focusField : null;
});
useEffect(() => {
  if (primaryBootstrapField === null) return undefined;
  const rafId = requestAnimationFrame(() => setPrimaryBootstrapField(null));
  return () => cancelAnimationFrame(rafId);
}, [primaryBootstrapField]);

// [rev17 plan D4] Focused-control effect. A zero-delay task runs after the Radix Sheet portal
// commits and its auto-focus settles, while keeping the focusField-driven target in the DOM.
// `preventScroll` keeps the page scroll position stable so the user's Detail scroll is not lost.
useEffect(() => {
  if (!open || !focusField) return undefined;
  const timeoutId = window.setTimeout(() => {
    const wrapper = document.querySelector<HTMLElement>(
      `[data-testid="txn-field-input-${focusField}"], [data-testid="txn-field-trigger-${focusField}"]`,
    );
    if (!wrapper) return;
    const target =
      wrapper.matches('input, textarea, select, button')
        ? wrapper
        : wrapper.querySelector<HTMLElement>('input, textarea, button');
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  });
  return () => window.clearTimeout(timeoutId);
}, [open, focusField]);

  // [Rev23 E1] Canonical Category FormField. Inserted into the primary map at the
  // exact position where categoryId sorts (order 50, between accountId @ 40 and
  // tags @ 60) so the visible form order is Type -> Amount -> Date -> Account ->
  // Category -> Tags. Preserves every E1 contract: FormItem/FormLabel/Select/
  // FormControl, data-testid="category-select", aria-label="Category", legacy
  // hidden anchor, 44px trigger, filteredCategories + None option, transfer
  // filtering, validation, emoji/name rendering, and the txn-field-row-categoryId
  // wrapper. The PRIMARY_FIELDS.filter chain still excludes categoryId for
  // transfer types, so this helper is only invoked in the non-transfer path.
  const renderCategoryField = (): React.ReactNode => {
    if (watchType === 'transfer') return null;
    const categorySpec = PRIMARY_FIELDS.find((s) => s.key === 'categoryId');
    if (!categorySpec) return null;
    const Icon = categorySpec.icon ?? Tag;
    return (
      <FormField
        control={form.control}
        name="categoryId"
        render={({ field }) => {
          const categoryValue = (field.value as string | undefined) ?? '';
          const selectedCategoryOption = categoryValue
            ? filteredCategories.find((cat) => cat.id === categoryValue)
            : undefined;
          return (
            <FormItem>
              <div
                data-testid="txn-field-row-categoryId"
                className="mb-2 grid grid-cols-[28px_minmax(100px,1fr)_minmax(0,2fr)] max-[374px]:grid-cols-[28px_minmax(0,1fr)] gap-x-3 gap-y-2 py-4 border-b border-border last:border-b-0 items-center sm:grid-cols-[28px_100px_minmax(0,1fr)]"
              >
                <div
                  data-testid="txn-field-cell-icon-categoryId"
                  className="shrink-0 flex items-center justify-start min-h-[20px]"
                >
                  {Icon ? (
                    <Icon className="h-[20px] w-[20px] text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <span className="block h-[20px] w-[20px]" aria-hidden="true" />
                  )}
                </div>
                <div data-testid="txn-field-cell-label-categoryId" className="min-w-0 break-normal">
                  <FormLabel className="m-0 flex flex-wrap items-baseline gap-x-1 gap-y-0 text-xs font-semibold text-foreground/80">
                    <span
                      data-testid="txn-field-label-title-categoryId"
                      className="min-w-0 inline-flex items-center min-h-[20px] whitespace-nowrap"
                    >
                      {categorySpec.label}
                    </span>
                    {(categorySpec.optional || categorySpec.tooltip) && (
                      <span
                        data-testid="txn-field-label-meta-categoryId"
                        className="flex items-center gap-0.5 whitespace-nowrap text-[10px] font-normal leading-3 text-muted-foreground"
                      >
                        {categorySpec.optional ? <OptionalTag /> : null}
                        {categorySpec.tooltip ? (
                          <span
                            data-testid="txn-field-tooltip-categoryId"
                            aria-label={categorySpec.tooltip}
                            className="inline-flex items-center"
                          >
                            <FieldTooltip text={categorySpec.tooltip} />
                          </span>
                        ) : null}
                      </span>
                    )}
                  </FormLabel>
                </div>
                <div data-testid="txn-field-cell-value-categoryId" className="min-w-0 space-y-1 max-[374px]:col-start-2">
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={categoryValue}
                      value={categoryValue}
                    >
                      <SelectTrigger
                        data-testid="category-select"
                        aria-label="Category"
                        className="min-h-[44px] min-w-[44px] w-full min-w-0 text-left"
                      >
                        <SelectValue placeholder="Select category">
                          {selectedCategoryOption ? (
                            <span
                              data-testid="txn-field-cell-emoji-categoryId"
                              className="inline-flex items-baseline gap-1"
                            >
                              {selectedCategoryOption.emoji ? (
                                <span aria-hidden="true">{selectedCategoryOption.emoji}</span>
                              ) : null}
                              <span>{selectedCategoryOption.name}</span>
                            </span>
                          ) : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {filteredCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {/*
                    Legacy compatibility shim: the original generic TransactionFieldRow
                    emitted a SelectTrigger with data-testid="txn-field-trigger-categoryId".
                    The Rev23 E1 canonical testid is "category-select" on the same node,
                    so we expose a parallel hidden anchor carrying the legacy id for any
                    downstream consumers that still query the legacy selector. The hidden
                    anchor is aria-hidden + sr-only so it never interferes with the visible
                    trigger.
                  */}
                  <span
                    data-testid="txn-field-trigger-categoryId"
                    aria-hidden="true"
                    className="sr-only"
                  />
                  <FormMessage data-testid="txn-field-error-categoryId" />
                </div>
              </div>
            </FormItem>
          );
        }}
      />
    );
  };


  return (
    <>
    <Sheet open={open} onOpenChange={guardedOnOpenChange}>
      <SheetContent
        side="right"
        className="w-[calc(100vw-3rem)] sm:max-w-[480px] lg:max-w-[860px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]"
        data-focus-field={focusField || undefined}
      >
        {focusField && (
          <span
            data-testid="modal-stack-meta-focus-field"
            data-value={focusField}
            aria-hidden="true"
            className="sr-only"
          />
        )}
        {/* [rev18] Compact header shell — integrates the stack-aware back affordance into the header row and removes the standalone ModalBreadcrumb row so Edit/Add carries exactly one back control. The `modal-header-shell` marker stays within the 88px compact-header budget at mobile widths. */}
<div
data-testid="modal-header-shell"
className="px-4 py-3 flex items-center gap-2 shrink-0 border-b"
>
{isEditing && Boolean(onBack) && (stack?.length ?? 0) > 1 && (
<Button
type="button"
variant="ghost"
className="min-h-[44px] min-w-[44px] h-11 w-11 shrink-0 p-0"
data-testid="form-back-btn"
onClick={onBack}
aria-label="Back to Detail"
>
<span aria-hidden="true">←</span>
<span className="sr-only">Back to Detail</span>
</Button>
)}
<div className="min-w-0 flex-1">
<SheetTitle className="text-base font-semibold leading-tight">
{isEditing ? 'Edit Transaction' : 'Add Transaction'}
</SheetTitle>
<SheetDescription className="sr-only">
{isEditing
? 'Update the transaction details below.'
: 'Fill in the details to create a new transaction.'}
</SheetDescription>
</div>
</div>

        {/* [rev17 plan D6] Single bounded internal scroll owner. SheetHeader stays fixed
            above and the SheetFooter (action boundary) stays fixed below. */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col flex-1 min-h-0 overflow-hidden"
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 min-w-0 scrollbar-elegant touch-pan-y overscroll-contain md:[scrollbar-gutter:stable]">

              {justRestored && (
                <DraftRestoredBanner
                  age={restoredAge}
                  onDismiss={dismissRestore}
                  className="mx-0 mt-4"
                />
              )}

              <div className="mt-4 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-4 min-w-0">
                <div className="min-w-0 flex flex-col">
{/* rev16 Phase D: Canonical primary scaffold — type, amount, date, accountId, categoryId, tags, notes, receipt, emoji. */}
{/* [Rev23 E1] categoryId is rendered via the local renderCategoryField()
   helper (defined above the return) so it appears in the primary sequence at
   the exact position where categoryId sorts (between accountId and tags),
   preserving the txn-field-row-categoryId wrapper + txn-field-cell-emoji-
   categoryId slot while exposing the canonical data-testid="category-select"
   on the SelectTrigger (combobox role, 44px, FormControl). The transfer type
   short-circuit is enforced both by the filter chain (excludes categoryId
   when watchType === 'transfer') and inside renderCategoryField itself. */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="mb-2 w-full min-w-0" data-testid="edit-description-section">
                  <FormLabel>Description<RequiredAsterisk /><FieldTooltip text="A short, searchable label. Aim for something you'll recognize months later, e.g., 'Starbucks', 'ACME Corp Salary', 'Electricity Bill'." /></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Grocery shopping" data-testid="txn-field-input-description" {...field} className="min-w-0 w-full" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

{PRIMARY_FIELDS
              .filter((spec) => primaryBootstrapField === null || spec.key === primaryBootstrapField)
              .filter((spec) => spec.key !== 'items')
              .filter((spec) => !(spec.key === 'categoryId' && watchType === 'transfer'))
              .filter((spec) => primaryBootstrapField === null || spec.key === primaryBootstrapField)
              .filter((spec) => spec.key !== 'items')
              .filter((spec) => !(spec.key === 'categoryId' && watchType === 'transfer'))
.sort((a, b) => a.order - b.order)
              .map((spec) => {
                if (spec.key === 'categoryId') {
                  return <Fragment key={spec.key}>{renderCategoryField()}</Fragment>;
                }
                const editRenderer = (rendererArgs: EditRendererArgs): React.ReactNode => {
                  const { field } = rendererArgs;
                  switch (spec.key) {
                    case 'type': {
                      const value = field.value as 'income' | 'expense' | 'transfer';
                      return (
                        <Select onValueChange={field.onChange} defaultValue={value} value={value}>
                          <SelectTrigger data-testid="txn-field-trigger-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="income">Income</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                            <SelectItem value="transfer">Transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    }
                    case 'amount': {
                      const amountField = field as unknown as {
                        onChange: (value: number) => void;
                        onBlur: () => void;
                        value: number;
                        name: string;
                        ref: React.Ref<HTMLInputElement>;
                      };
                      return (
                        <div data-testid="txn-field-input-amount" className="text-left">
                          <Input
                            type="number"
                            step={currencyStep}
                            min={currencyMin}
                            placeholder="0.00"
                            data-testid="amount-input"
                            value={amountField.value ?? ''}
                            onChange={(e) => amountField.onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                            onBlur={amountField.onBlur}
                            name={amountField.name}
                            ref={amountField.ref}
                            className="min-w-0 w-full px-1 text-center"
                          />
                        </div>
                      );
                    }
                    case 'date': {
                      const dateValue = field.value as Date | undefined;
                      return (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              data-testid="txn-field-input-date"
                            className={cn(
                                'w-full min-w-0 overflow-hidden pl-3 text-left font-normal whitespace-normal',
                                !dateValue && 'text-muted-foreground'
                              )}
                            >
                              <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere] leading-tight">
                                {dateValue ? formatInUserTimezone(dateValue, 'date-only') : 'Pick a date'}
                              </span>
                              <CalendarIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={dateValue}
                              onSelect={field.onChange}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      );
                    }
                    case 'accountId': {
                      const accountValue = (field.value as string | undefined) ?? '';
                      const selectedAccountOption = accounts.find((account) => account.id === accountValue);
                      const selectedAccountLabel = selectedAccountOption
                        ? `${selectedAccountOption.name} (${humanizeAccountType(selectedAccountOption.accountType)})`
                        : undefined;
                      return (
                        <Select onValueChange={field.onChange} defaultValue={accountValue} value={accountValue}>
                          <SelectTrigger
                            data-testid="txn-field-trigger-accountId"
                            className="text-left"
                            aria-label={selectedAccountLabel}
                            title={selectedAccountLabel}
                          >
                            <SelectValue placeholder="Select account">
                              {selectedAccountOption ? (
                                <span
                                  data-testid="txn-field-cell-emoji-accountId"
                                  className="inline-flex items-baseline gap-1"
                                >
                                  {selectedAccountOption.emoji ? (
                                    <span aria-hidden="true">{selectedAccountOption.emoji}</span>
                                  ) : null}
                                  <span>{selectedAccountOption.name}</span>
                                </span>
                              ) : null}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {accounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {account.emoji ? `${account.emoji} ` : ''}{account.name} ({humanizeAccountType(account.accountType)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                    case 'categoryId': {
                      // [Rev23 E1] Removed. categoryId is special-cased out of the
                      // generic TransactionFieldRow by the filter chain above (see
                      // the `[Rev23 E1]` comment before PRIMARY_FIELDS.map) and is
                      // rendered by the local FormField/FormItem below. If a future
                      // edit ever reaches this branch, fall through to null rather
                      // than re-rendering through the generic map (which would
                      // re-introduce the duplicate category path).
                      return null;
                    }
                    case 'tags': {
                      const tagsValue = (field.value as string[] | undefined) ?? [];
                      return (
                        <div data-testid="txn-field-input-tags">
                          <TagInput
                            value={tagsValue}
                            onChange={field.onChange}
                            suggestions={tagSuggestions}
                            tagStats={tagSuggestionStats}
                            placeholder="Add tags..."
                          />
                        </div>
                      );
                    }
                    case 'notes': {
                      const notesValue = (field.value as string | undefined) ?? '';
                      return (
                        <Textarea
                          placeholder="Add any additional notes..."
                          className="resize-none"
                          data-testid="txn-field-input-notes"
                          value={notesValue}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      );
                    }
                    case 'receipt': {
                      const hasReceipt = !!(receiptId || pendingReceiptId || transaction?.receiptAttachment?.filename);
                      return (
                        <div data-testid="txn-field-input-receipt" className="min-w-0 space-y-2">
                          {hasReceipt ? (
                            <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/30">
                              <Paperclip className="h-5 w-5 text-muted-foreground shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">
                                  {(transaction?.receiptAttachment?.filename ?? pendingReceiptFilename) || 'Receipt attached'}
                                </p>
                                {(transaction?.receiptAttachment?.size ?? pendingReceiptSize) != null && (
                                  <p className="text-[11px] text-muted-foreground">
                                    {(transaction?.receiptAttachment?.size ?? pendingReceiptSize ?? 0) >= 1024 * 1024
                                      ? `${((transaction?.receiptAttachment?.size ?? pendingReceiptSize ?? 0) / 1024 / 1024).toFixed(1)} MB`
                                      : `${((transaction?.receiptAttachment?.size ?? pendingReceiptSize ?? 0) / 1024).toFixed(0)} KB`}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-1 shrink-0">
                                <IconButton
                                  ref={receiptViewTriggerRef}
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  tooltip="View receipt"
                                  onClick={handleReceiptView}
                                  data-testid="transaction-form-receipt-view-btn"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </IconButton>
                                <IconButton
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  tooltip="Remove link"
                                  onClick={handleReceiptDelete}
                                  disabled={receiptDeleting}
                                  data-testid="transaction-form-receipt-unlink-btn"
                                >
                                  {receiptDeleting ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                </IconButton>
                              </div>
                            </div>
                          ) : (
                            <>
                              <input
                                ref={receiptInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                                className="hidden"
                                onChange={handleReceiptUpload}
                              />
                              <Button
                                type="button"
                                variant="outline"
                            className="w-full min-w-0 min-h-[44px] px-1 text-xs whitespace-normal leading-tight"
                                onClick={() => receiptInputRef.current?.click()}
                                disabled={receiptUploading}
                              >
                                {receiptUploading ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Camera className="h-4 w-4 mr-1 shrink-0" />
                                )}
                                {receiptUploading ? 'Uploading…' : 'Attach Receipt'}
                              </Button>
                            </>
                          )}
                        </div>
                      );
                    }
                    case 'emoji': {
                      const emojiValue = (field.value as string | undefined) ?? '';
                      return (
                        <div data-testid="txn-field-input-emoji">
                          <EmojiPicker value={emojiValue} onChange={field.onChange} />
                        </div>
                      );
                    }
                    default:
                      return null;
                  }
                };

                const fieldOrderClass = {
                  type: 'order-10',
                  amount: 'order-20',
                  date: 'order-30',
                  accountId: 'order-40',
                  categoryId: 'order-50',
                  tags: 'order-60',
                  receipt: 'order-80',
                  notes: 'order-90',
                  emoji: 'order-100',
                }[spec.key] ?? 'order-110';

                return (
                  <div key={spec.key} className={fieldOrderClass}>
                    <TransactionFieldRow
                      field={{ ...spec, editRenderer }}
                      mode="edit"
                      form={form}
                      value={undefined}
                    />
                  </div>
                );
              })}
            {/* rev16 Phase F: Secondary fields (currency, discountAmount) consolidated in a collapsible disclosure. */}
            <SecondaryFieldsDisclosure
              fields={secondaryFields}
              mode="edit"
              form={form}
              defaultOpen={false}
              autoOpen={watchTransactionDiscount > 0}
              className="order-[110] mb-2"
            />

            {/* To Account (Transfer only) */}
            {watchType === 'transfer' && (
              <FormField
                control={form.control}
                name="toAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>To Account<RequiredAsterisk /><FieldTooltip text="The destination account for the transfer. Balances on both accounts will be updated." /></FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select destination account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accounts
                          .filter((a) => a.id !== watchAccountId)
                          .map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.emoji ? `${account.emoji} ` : ''}{account.name} ({humanizeAccountType(account.accountType)})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* T80P1: Line Items Section — between Category and Notes, hidden for transfers */}
            {watchType !== 'transfer' && (
              <div className="order-[70] space-y-3">
                <Collapsible open={itemsEnabled} onOpenChange={() => {}}>
                  <div className="flex items-center justify-between">
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                        onClick={() => handleItemsToggle(!itemsEnabled)}
                      >
                        <ChevronDown className={cn('h-4 w-4 transition-transform', itemsEnabled && 'rotate-180')} />
                        Line Items
                        <span className="text-xs text-muted-foreground font-normal">
                          (Itemize this transaction)
                        </span>
                      </button>
                    </CollapsibleTrigger>
                    <Switch
                      checked={itemsEnabled}
                      onCheckedChange={handleItemsToggle}
                      aria-label="Toggle line items"
                    />
                  </div>

                  {/* Inline remove confirmation */}
                  {showRemoveConfirm && (
                    <div className="flex items-center gap-2 mt-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
                      <p className="text-sm text-destructive flex-1">
                        Remove all {fields.length} item{fields.length !== 1 ? 's' : ''}?
                      </p>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleConfirmRemove}
                      >
                        Remove
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowRemoveConfirm(false)}
                      >
                        Keep
                      </Button>
                    </div>
                  )}

                  <CollapsibleContent>
                    <div className="mt-3 space-y-3" aria-live="polite">
                      {/* Summary Bar */}
                      {fields.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <span className="text-muted-foreground">
                              Gross Subtotal: <span className="font-medium text-foreground" data-testid="item-subtotal-value">{formatActiveMoney(itemSubtotal)}</span>
                            </span>
                            <span className="text-muted-foreground text-right">
                              Discounts Subtracted: <span className="font-medium text-foreground" data-testid="item-discount-total-value">-{formatActiveMoney(itemDiscountTotal)}</span>
                            </span>
                            <span className="text-muted-foreground">
                              Net Items Total: <span className="font-medium text-foreground" data-testid="item-net-total-value">{formatActiveMoney(itemNetTotal)}</span>
                            </span>
                            <span className={cn('text-muted-foreground text-right', isOverBudget && 'text-destructive font-medium')}>
                              {remaining < 0 ? 'Over Cap by:' : 'Unallocated:'}
                              {' '}
                              <span
                                className="font-medium"
                                data-testid="item-remaining-value"
                                title={remaining < 0 ? 'Line items exceed the transaction net cap.' : 'Amount not yet covered by line items. Item discounts reduce an item\'s cost, which increases this unallocated amount.'}
                              >
                                {formatActiveMoney(Math.abs(remaining))}
                              </span>
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">
                              Gross Cap: <span className="font-medium text-foreground" data-testid="transaction-net-cap-value">{formatActiveMoney(transactionNetCap)}</span>
                            </span>
                          </div>
                          <Progress
                            value={itemizationPercent}
                            className={cn('h-2', isOverBudget && '[&>div]:bg-destructive')}
                            aria-label={`Itemization progress: ${Math.round(itemizationPercent)}% of transaction net cap`}
                          />
                          {isOverBudget && (
                            <p className="text-xs text-destructive">
                              Net items total exceeds transaction net cap by {formatActiveMoney(itemNetTotal - transactionNetCap)}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Item Accordion */}
                      {fields.map((field, index) => {
                        const isExpanded = expandedItems.has(index);
                        const isLast = index === fields.length - 1;
                        const itemErrors = form.formState.errors.items;
                        const hasError = itemErrors && Array.isArray(itemErrors) && itemErrors[index];
                        const toggleItem = () => {
                          setExpandedItems(prev => {
                            const next = new Set<number>();
                            if (!prev.has(index)) next.add(index);
                            return next;
                          });
                        };
                        return (
                        <div key={field.id} className={cn(
                          !isLast && !isExpanded && 'border-b border-border/30'
                        )}>
                          {/* Collapsed row — always visible */}
                          <div
                            className={cn(
                              'flex items-center w-full gap-2 px-3 py-2 min-h-[44px] rounded-lg transition-colors',
                              'hover:bg-muted/40',
                              isExpanded && 'bg-muted/30',
                              hasError && !isExpanded && 'border-l-2 border-l-destructive'
                            )}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-center gap-2 text-left"
                              aria-expanded={isExpanded}
                              onClick={toggleItem}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  toggleItem();
                                }
                              }}
                            >
                              <span className="shrink-0 w-6 text-center text-sm">
                                {watchItems[index]?.emoji || <span className="text-xs text-muted-foreground">{index + 1}.</span>}
                              </span>
                              <span className="flex-1 min-w-0 text-sm truncate">
                                {watchItems[index]?.description || 'Untitled item'}
                              </span>
                              {(watchItems[index]?.quantity ?? 1) > 1 && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 tabular-nums shrink-0">
                                  ×{watchItems[index]?.quantity}
                                </Badge>
                              )}
                              <span className="text-sm font-medium tabular-nums shrink-0 min-w-[60px] text-right">
                                {formatActiveMoney(((Number(watchItems[index]?.amount) || 0) * (Number(watchItems[index]?.quantity) || 1)) - clampNonNegative(Number(watchItems[index]?.discountAmount) || 0))}
                              </span>
                              <ChevronDown className={cn(
                                'h-3.5 w-3.5 text-muted-foreground/50 shrink-0 transition-transform duration-200',
                                isExpanded && 'rotate-180'
                              )} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="shrink-0 h-8 w-8 min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive rounded-md"
                              ref={(el) => { itemDeleteRefs.current[index] = el; }}
                              data-testid={`item-delete-btn-${index}`}
                              aria-label={`Remove item ${index + 1}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          {/* Expanded form panel — layout changes immediately; opacity transition only */}
                          <div
                            className="transition-opacity duration-200 ease-in-out"
                            hidden={!isExpanded}
                          >
                            <div className="overflow-hidden min-h-0">
                              <div className="px-3 pb-3 pt-2 space-y-3 bg-muted/10 rounded-b-lg border-b border-border/20">
                                {/* Description + Qty + Amount */}
                                <div className="grid grid-cols-1 sm:grid-cols-[1fr_70px_120px] gap-3">
                                  <FormField
                                    control={form.control}
                                    name={`items.${index}.description`}
                                    render={({ field: itemField }) => (
                                      <FormItem>
                                        <FormLabel className="text-xs">Description<RequiredAsterisk /></FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder="Item description"
                                            {...itemField}
                                            ref={(el) => {
                                              itemDescRefs.current[index] = el;
                                              if (typeof itemField.ref === 'function') itemField.ref(el);
                                            }}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`items.${index}.quantity`}
                                    render={({ field: itemField }) => (
                                      <FormItem>
                                        <FormLabel className="text-xs">Qty</FormLabel>
                                        <FormControl>
                                          <Input type="number" step={1} min={1} max={9999} placeholder="1" className="text-center" data-testid={`item-qty-input-${index}`} {...itemField} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`items.${index}.amount`}
                                    render={({ field: itemField }) => (
                                      <FormItem>
                                        <FormLabel className="text-xs">Amount ({currencySymbol})<RequiredAsterisk /></FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            step={currencyStep}
                                            min={currencyMin}
                                            placeholder="0.00"
                                            data-testid={`item-amount-input-${index}`}
                                            {...itemField}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                {/* Item discount and live item summary */}
                                <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-3 items-end">
                                  <FormField
                                    control={form.control}
                                    name={`items.${index}.discountAmount`}
                                    render={({ field: itemField }) => (
                                      <FormItem>
                                        <FormLabel className="text-xs">Discount ({currencySymbol})<OptionalTag /></FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            step={currencyStep}
                                            min={0}
                                            placeholder="0.00"
                                            data-testid={`item-discount-input-${index}`}
                                            value={itemField.value ?? ''}
                                            onChange={(e) => itemField.onChange(e.target.value === '' ? undefined : e.target.value)}
                                            onBlur={itemField.onBlur}
                                            name={itemField.name}
                                            ref={itemField.ref}
                                          />
                                        </FormControl>
                                        <FormMessage data-testid={`item-discount-error-${index}`} />
                                      </FormItem>
                                    )}
                                  />

                                  <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                                    <span>
                                      Gross Line Total: <span className="font-medium text-foreground" data-testid={`item-gross-value-${index}`}>{formatActiveMoney(((Number(watchItems[index]?.amount) || 0) * (Number(watchItems[index]?.quantity) || 1)))}</span>
                                    </span>
                                    <span>
                                      Discounts Subtracted: <span className="font-medium text-foreground" data-testid={`item-discount-value-${index}`}>-{formatActiveMoney(clampNonNegative(Number(watchItems[index]?.discountAmount) || 0))}</span>
                                    </span>
                                    <span>
                                      Net Line Total: <span className="font-medium text-foreground" data-testid={`item-net-value-${index}`}>{formatActiveMoney((((Number(watchItems[index]?.amount) || 0) * (Number(watchItems[index]?.quantity) || 1)) - clampNonNegative(Number(watchItems[index]?.discountAmount) || 0)))}</span>
                                    </span>
                                  </div>
                                </div>

                                {/* Category (Detailed density: selectable, otherwise badge/read-only) */}
                                {itemDisplayDensity === 'detailed' ? (
                                  <FormField
                                    control={form.control}
                                    name={`items.${index}.categoryId`}
                                    render={({ field: itemField }) => (
                                      <FormItem>
                                        <FormLabel className="text-xs">Category<OptionalTag /></FormLabel>
                                        <Select
                                          onValueChange={(value) => itemField.onChange(value === 'inherit' ? '' : value)}
                                          value={itemField.value || 'inherit'}
                                        >
                                          <FormControl>
                                            <SelectTrigger data-testid={`item-category-select-${index}`}>
                                              <SelectValue placeholder="Use transaction category" />
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            <SelectItem value="inherit">Use transaction category</SelectItem>
                                            {filteredCategories.map((cat) => (
                                              <SelectItem key={cat.id} value={cat.id}>
                                                {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : (
                                  <div className="space-y-1">
                                    <FormLabel className="text-xs">Category<OptionalTag /></FormLabel>
                                    <Badge variant="outline" className="w-fit text-[11px]">
                                      {(() => {
                                        const itemCategoryId = watchItems[index]?.categoryId;
                                        const itemCategory = filteredCategories.find((cat) => cat.id === itemCategoryId);
                                        if (!itemCategory) return 'Use transaction category';
                                        return `${itemCategory.emoji ? `${itemCategory.emoji} ` : ''}${itemCategory.name}`;
                                      })()}
                                    </Badge>
                                  </div>
                                )}
                                {/* Notes */}
                                <FormField
                                  control={form.control}
                                  name={`items.${index}.notes`}
                                  render={({ field: itemField }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs">Notes<OptionalTag /></FormLabel>
                                      <FormControl>
                                        <Textarea
                                          placeholder="Additional notes..."
                                          className="resize-none text-sm"
                                          rows={2}
                                          {...itemField}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                {/* Emoji */}
                                <FormField
                                  control={form.control}
                                  name={`items.${index}.emoji`}
                                  render={({ field: itemField }) => (
                                    <FormItem>
                                      <FormLabel className="text-xs">Emoji<OptionalTag /></FormLabel>
                                      <FormControl>
                                        <EmojiPicker value={itemField.value} onChange={itemField.onChange} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}

                      {/* Add Item Button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-dashed min-h-[44px]"
                        onClick={handleAddItem}
                        disabled={fields.length >= 50}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item {fields.length >= 50 && '(max 50)'}
                      </Button>

                      {/* Form-level items error */}
                      {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive" data-testid="items-net-total-error">
                          {form.formState.errors.items.message}
                        </p>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}

                </div>
                <div className="min-w-0 space-y-4">
                  {/* [rev20 L3-B] Mobile/tablet preview toggle. Sits outside the pane wrapper so it remains
                      reachable when the pane is collapsed below `lg`. Hidden at `lg` and above where the pane
                      is always visible in the desktop two-column layout. On desktop the node is unmounted
                      (not just CSS-hidden) so DOM-count assertions stay truthful; `lg:hidden` is kept as a
                      CSS safety boundary against stale `'desktop' | true` server snapshots during HMR. */}
                  {!isDesktop && (
                    <Button
                      type="button"
                      variant="outline"
                      className="lg:hidden w-full min-h-[44px] min-w-[44px]"
                      data-testid="receipt-preview-pane-mobile-toggle"
                      aria-expanded={previewOpen}
                      aria-controls="receipt-preview-pane"
                      onClick={() => setPreviewOpen((prev) => !prev)}
                    >
                      Preview
                    </Button>
                  )}
                  {/* Pane wrapper owns the conditional visibility. `id="receipt-preview-pane"` is the
                      target referenced by the toggle's `aria-controls`. At `lg` the wrapper is always
                      visible via the `lg:block` override; below `lg` it follows the toggle state. */}
                  <div
                    id="receipt-preview-pane"
                    className={cn('min-w-0 lg:sticky lg:top-4', previewOpen ? 'block' : 'hidden lg:block')}
                  >
                    <ReceiptPreviewPane
                      className="lg:max-h-[calc(100dvh-180px)]"
                      description={form.watch('description') || '(no description)'}
                      category={categories.find((cat) => cat.id === form.watch('categoryId'))?.name}
                      amount={watchAmount}
                      transactionDiscountAmount={watchTransactionDiscount}
                      currency={activeCurrency}
                      items={itemsEnabled ? watchItems : []}
                      // [Rev23 G4] Edit-only acknowledgement of the over-allocation
                      // advisory. Detail/read-only callers omit this and the warning
                      // stays persistently visible.
                      dismissible
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* [rev17 plan D6] Action boundary footer. Cancel + Create/Update. Sits
                outside the body scroll wrapper so the controls remain reachable at 320x568
                even when the form body is long. Each control is at the 44px touch-target
                floor per FinTrack mobile-first rules. */}
            <SheetFooter
              data-horizontal-rail="true"
              className="shrink-0 border-t bg-background px-4 sm:px-6 py-3 gap-2 flex-row flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden touch-pan-x"
            >
              <Button
                type="button"
                variant="outline"
                className="min-h-[44px]"
                onClick={() => guardedOnOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-h-[44px]"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
      <DiscardChangesDialog
        open={showDiscard}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        onSaveDraft={!isEditing ? saveDraftAndClose : undefined}
      />

      <ReceiptViewerDialog
        open={receiptViewerOpen}
        onOpenChange={setReceiptViewerOpen}
        receiptId={receiptId || pendingReceiptId || null}
        returnFocusRef={receiptViewTriggerRef}
        metadata={transaction?.receiptAttachment ? {
          filename: transaction.receiptAttachment.filename,
          size: transaction.receiptAttachment.size,
          mimetype: transaction.receiptAttachment.mimetype,
        } : pendingReceiptFilename ? {
          filename: pendingReceiptFilename,
          size: pendingReceiptSize,
          mimetype: pendingReceiptMimeType,
        } : undefined}
      />
    </>
  );
}

// ── Hook: useMediaQuery ─────────────────────────────────────────────────────


/**
 * Subscribe to a CSS media query and return a boolean reflecting whether it
 * currently matches. Mirrors `frontend/src/components/dashboard/insights-manager/
 * InsightsGraphPreviewModal.tsx`'s `useMediaQuery`: lazy-initialized from
 * `window.matchMedia(query).matches` (so desktop tests do not see a transient
 * mounted button after the form is ready), with SSR-safe fallback, modern
 * `addEventListener` plus legacy `addListener` for older Safari, and proper
 * cleanup on unmount or query change.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
